// Snapshot client for the per-tile camera previews on the live wall.
//
// BFF route: GET /api/cameras/{doorbotId}/poster
// Behavior  : returns JSON {"url": "<signed cdn url>", "expires_at":
//             "<ISO-8601>"}. The browser fetches the bytes directly from
//             Ring's CDN via <img src={url}>, which avoids the
//             BFF→browser proxy round-trip (~3s) the bytes-inline
//             implementation paid before.
//
// Why <img> against the CDN works (the historical 302 attempt didn't):
//   <img> is a "no-cors" request — no preflight, no custom headers.
//   The browser walks the URL, the CDN serves bytes, no Origin
//   negotiation. The signed URL itself carries the auth.
//
// Status mapping:
//   200 → ok with {url, expiresAt}
//   401 → ApiError thrown (the session provider handles the redirect)
//   403 → no-access (permission denied)
//   404 → no-preview (no recent snapshot — render fallback tile)
//   5xx / network → error (transient, retry next tick)
//
// We DO replicate the X-Acting-Role + 401 behavior of the standard api
// wrapper. Keep this in sync with client.ts when that contract evolves.

import { ApiError, getCurrentRole } from "./client";

const ACTING_ROLE_HEADER = "X-Acting-Role";

/** Build the BFF poster path for a given camera id. */
export function snapshotPath(deviceId: string): string {
  return `/api/cameras/${encodeURIComponent(deviceId)}/poster`;
}

export type SnapshotFetchResult =
  | { kind: "ok"; url: string; expiresAt: string | null }
  | { kind: "no-access" }
  | { kind: "no-preview" }
  | { kind: "error"; status: number };

interface PosterEnvelope {
  url: string;
  expires_at?: string | null;
  etag?: string | null;
}

/**
 * Fetch the latest poster URL for a camera. The returned `url` is a
 * short-lived signed URL the caller renders directly in <img src=...>.
 *
 * Status mapping (matches LLD 09 §8.2 row 7 + LLD 01 §6.1 read-collapse
 * semantics):
 *   200 → ok with {url, expiresAt}
 *   403 → no-access
 *   404 → no-preview
 *   5xx / network → error (transient, retry next interval)
 */
export async function fetchSnapshot(
  deviceId: string,
  signal?: AbortSignal,
): Promise<SnapshotFetchResult> {
  const path = snapshotPath(deviceId);

  const headers = new Headers();
  headers.set("Accept", "application/json");
  // Replicate the api wrapper: every /api/* call carries X-Acting-Role.
  // If the role isn't set, treat the request as "error" — the page-
  // level role guard will already have redirected to the picker; we
  // shouldn't crash polling tiles in the meantime.
  const role = getCurrentRole();
  if (role !== null && role !== "") {
    headers.set(ACTING_ROLE_HEADER, role);
  } else {
    return { kind: "error", status: 0 };
  }

  let response: Response;
  try {
    response = await fetch(path, {
      method: "GET",
      headers,
      credentials: "same-origin",
      signal,
    });
  } catch {
    return { kind: "error", status: 0 };
  }

  if (response.status === 401) {
    throw new ApiError(401, "unauthorized", "Unauthorized", undefined, null);
  }
  if (response.status === 403) {
    void response.body?.cancel();
    return { kind: "no-access" };
  }
  if (response.status === 404) {
    void response.body?.cancel();
    return { kind: "no-preview" };
  }
  if (!response.ok) {
    void response.body?.cancel();
    return { kind: "error", status: response.status };
  }

  let envelope: PosterEnvelope;
  try {
    envelope = (await response.json()) as PosterEnvelope;
  } catch {
    return { kind: "error", status: 0 };
  }
  if (!envelope || typeof envelope.url !== "string" || envelope.url.length === 0) {
    return { kind: "error", status: response.status };
  }
  return {
    kind: "ok",
    url: envelope.url,
    expiresAt: envelope.expires_at ?? null,
  };
}
